import { defineStore } from 'pinia';
import { permissionService } from '@/services/permissionService';
import { useAuthStore } from './auth';
import { isLockedRole } from '@/utils/permissions';

export const usePermissionsStore = defineStore('permissions', {
  state: () => ({
    // Current user's permissions
    myPermissions: {},
    
    // All available permissions organized by category
    permissionCategories: {},
    
    // Selected user for management
    selectedUser: null,
    selectedUserPermissions: {},
    
    // Role permissions cache
    rolePermissions: {},
    
    // UI state
    loading: false,
    error: null,
    initialized: false,
    
    // Cache
    lastFetch: null,
    cacheTimeout: 5 * 60 * 1000 // 5 minutes
  }),

  getters: {
    // Check if current user has a permission
    hasPermission: (state) => (permissionCode) => {
      const authStore = useAuthStore();
      if (isLockedRole(authStore.user?.role)) {
        return true;
      }
      return state.myPermissions[permissionCode] === true;
    },

    // Check multiple permissions (AND)
    hasAllPermissions: (state) => (codes) => {
      const authStore = useAuthStore();
      if (isLockedRole(authStore.user?.role)) {
        return true;
      }
      return codes.every(code => state.myPermissions[code] === true);
    },

    // Check multiple permissions (OR)
    hasAnyPermission: (state) => (codes) => {
      const authStore = useAuthStore();
      if (isLockedRole(authStore.user?.role)) {
        return true;
      }
      return codes.some(code => state.myPermissions[code] === true);
    },

    // Get permissions for a specific category
    getCategoryPermissions: (state) => (category) => {
      return state.permissionCategories[category] || {};
    },

    // Check if cache is valid
    isCacheValid: (state) => {
      if (!state.lastFetch) return false;
      return Date.now() - state.lastFetch < state.cacheTimeout;
    }
  },

  actions: {
    // Initialize permissions for current user
    async initMyPermissions(force = false) {
      // Skip if already initialized (unless forced)
      if (!force && this.initialized) {
        return;
      }
      
      if (!force && this.isCacheValid) return;

      // Set initialized flag immediately to prevent concurrent calls
      this.initialized = true;

      try {
        this.loading = true;
        const data = await permissionService.getMyPermissions();

        // Transform permission objects to boolean values
        const transformedPermissions = {};
        if (data?.permissions) {
          Object.entries(data.permissions).forEach(([code, permissionObj]) => {
            // Handle both object format {granted: true} and direct boolean format
            if (typeof permissionObj === 'boolean') {
              transformedPermissions[code] = permissionObj;
            } else if (permissionObj && typeof permissionObj === 'object') {
              // Object format from backend: {granted: true/false, source: 'role'/'user', inherited: true/false}
              transformedPermissions[code] = permissionObj.granted === true;
            } else {
              transformedPermissions[code] = false;
            }
          });
        }

        this.myPermissions = transformedPermissions;
        this.lastFetch = Date.now();
        
        // Cache in localStorage
        localStorage.setItem('userPermissions', JSON.stringify({
          permissions: transformedPermissions,
          timestamp: Date.now()
        }));
      } catch (error) {
        this.error = error.message;
        console.error('❌ Permission Store: Failed to load permissions:', error);
        console.error('   - Error details:', error.response?.data);
        console.error('   - Status:', error.response?.status);
        
        // Try to load from cache if available
        const cached = localStorage.getItem('userPermissions');
        if (cached) {
          try {
            const { permissions, timestamp } = JSON.parse(cached);
            if (Date.now() - timestamp < this.cacheTimeout) {
              // Transform cached permissions if they're in the old object format
              let transformedPermissions = permissions;
              if (typeof Object.values(permissions)[0] === 'object') {
                transformedPermissions = {};
                Object.entries(permissions).forEach(([code, permissionObj]) => {
                  transformedPermissions[code] = permissionObj?.granted === true;
                });
              }
              
              this.myPermissions = transformedPermissions;
              this.lastFetch = timestamp;
              // Keep initialized as true since we have cached data
              return;
            }
          } catch (cacheError) {
            console.error('   - Failed to parse cache:', cacheError);
          }
        }
        
        // Reset initialized flag if we couldn't load permissions
        this.initialized = false;
      } finally {
        this.loading = false;
      }
    },

    // Load all permission categories
    async loadPermissionCategories(forceRefresh = false) {
      try {
        this.loading = true;
        // Clear cache if force refresh
        if (forceRefresh) {
          this.permissionCategories = {};
          this.lastFetch = null;
        }
        this.permissionCategories = await permissionService.getPermissionCategories();
        this.lastFetch = Date.now();
      } catch (error) {
        this.error = error.message;
        console.error('Failed to load permission categories:', error);
      } finally {
        this.loading = false;
      }
    },

    // Load permissions for a specific user
    async loadUserPermissions(userId) {
      try {
        this.loading = true;
        const data = await permissionService.getUserPermissions(userId);
        this.selectedUser = { id: userId, role: data.role };
        this.selectedUserPermissions = data.permissions;
      } catch (error) {
        this.error = error.message;
        console.error('Failed to load user permissions:', error);
      } finally {
        this.loading = false;
      }
    },

    // Update a single permission
    async updatePermission(userId, permissionCode, granted, options = {}) {
      try {
        if (granted) {
          await permissionService.grantPermission(userId, permissionCode, options);
        } else {
          await permissionService.revokePermission(userId, permissionCode, options.reason);
        }
        
        // Update local state
        if (this.selectedUser?.id === userId) {
          this.selectedUserPermissions[permissionCode] = {
            granted,
            source: 'user',
            inherited: false
          };
        }
        
        return { success: true };
      } catch (error) {
        this.error = error.message;
        return { success: false, error: error.message };
      }
    },

    // Bulk update permissions
    async bulkUpdatePermissions(userId, permissions, reason) {
      try {
        this.loading = true;
        const result = await permissionService.bulkUpdatePermissions(userId, permissions, reason);
        
        // Reload user permissions to ensure consistency
        await this.loadUserPermissions(userId);
        
        return result;
      } catch (error) {
        this.error = error.message;
        return { success: false, error: error.message };
      } finally {
        this.loading = false;
      }
    },

    // Clear cache and reload
    async refreshPermissions() {
      localStorage.removeItem('userPermissions');
      this.lastFetch = null;
      this.initialized = false;
      await this.initMyPermissions(true);
    },

    // Load permissions for a specific role
    async loadRolePermissions(role) {
      // Check if already cached
      if (this.rolePermissions[role]) {
        return this.rolePermissions[role];
      }

      try {
        this.loading = true;
        const response = await permissionService.getRolePermissions(role);
        this.rolePermissions[role] = response.permissions || {};
        return this.rolePermissions[role];
      } catch (error) {
        console.error('Failed to load role permissions:', error);
        this.error = error.message;
        return {};
      } finally {
        this.loading = false;
      }
    }
  }
});